import type { PropsWithChildren } from 'react'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { NavWrapper } from 'components/OfferPage/styles'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  arePricesLoading,
  getFromDurationLabel,
  getCheapestSelectedPackageOptionWithDefault,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { addFlightPrice } from 'lib/offer/pricing'
import { getPriceDisplayFunction, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import FlashHotelWePriceMatch from './FlashHotelWePriceMatch'
import clsx from 'clsx'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'

const StyledNavWrapper = styled(NavWrapper)`
  align-items: center;

  ${mediaQueryUp.tablet} {
    height: initial;
  }

  ${mediaQueryUp.desktop} {
    height: ${rem(60)};
  }
`

const ContentWrapper = styled(Group)`
  ${mediaQueryOnly.tablet} {
    padding-top: ${rem(16)};
  }

  &.has-top-padding {
    padding-top: ${rem(8)};
  }
`

const PriceSummarySection = styled(CSSBreakpoint)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: auto;
  }

  ${mediaQueryUp.desktop} {
    padding-left: ${rem(12)};
    justify-content: flex-end;
    margin-left: auto;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const PriceSummary = styled(Group)`
  position: relative;
`

interface Props {
  offer: App.Offer
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
  cheapestSelectedOption: App.OfferPackageOptionView
  className?: string
  durationLabel: string
  availableForPurchase?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
  rooms: Array<OfferPageRoom>
}

function FlashHotelStickyHeader(props: PropsWithChildren<Props>) {
  const {
    offer,
    onViewPackageOptions,
    children,
    calendarsLoading,
    className,
    durationLabel,
    cheapestSelectedOption: packageView,
    availableForPurchase,
    charterBundleRoute,
    priceMode,
    rooms,
  } = props

  const intl = useIntl()
  const {
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const dispatch = useOfferPageDispatch()

  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag, !charterBundleRoute)

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }

  const showPriceWithFlights = !!(offer.bundledWithFlightsOnly || charterBundleRoute)

  const [pricingToDisplay, memberPricingToDisplay] = useMemo(() => {
    let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
    let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
    if (showPriceWithFlights) {
      if (flightPrice) {
        pricingToDisplay = addFlightPrice(pricingToDisplay, flightPrice)
        memberPricingToDisplay = memberPricingToDisplay && addFlightPrice(memberPricingToDisplay, flightPrice)
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    return [pricingToDisplay, memberPricingToDisplay]
  }, [hotelPricing, hotelMemberPricing, flightPrice, showPriceWithFlights])

  const saleUnit = offer.bundledWithFlightsOnly ? 'package*' : offer.saleUnit

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  return (
    <StyledNavWrapper className={className}>
      <LayoutContainer>
        <ContentWrapper
          className={clsx({ 'has-top-padding': !!isWePriceMatchEnabled })}
          direction="horizontal"
          tabletDirection="vertical"
          desktopVerticalAlign="center"
          desktopDirection="horizontal"
          desktopHorizontalAlign="space-between"
          tabletGap={16}
        >
          {children}
          <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
            <PriceSummarySection>
              <PriceSummary direction="vertical" horizontalAlign="start" desktopHorizontalAlign="end">
                <LoadingIndicator visible={calendarsLoading} floating opaque />
                {!availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED && <BodyText variant="large" weight="bold">
                  <FormattedMessage
                    id="DmAweJrnch"
                    defaultMessage="This offer has<br></br> SOLD OUT"
                    description="Message shown when a flash hotel offer has sold out"
                    values={{
                      br: () => <CSSBreakpoint as="br" max="mobile" />,
                    }}
                  />
                </BodyText>}
                {availableForPurchase && pricingToDisplay && <>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceGuard
                    showMemberPrice={!!memberPricingToDisplay}
                    fallbackPrice={<>
                      <PriceRowPrice
                        size="S"
                        price={convertToDisplay(pricingToDisplay.price)}
                        saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                        data-testid="flash-hotel-sticky-header-price"
                      />
                      {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
                    </>}
                  >
                    <PriceRowLuxPlusPrice
                      size="M"
                      memberPrice={convertToDisplay(memberPricingToDisplay?.price ?? 0)}
                      saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                      logoPlacement="leading"
                    />
                    {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
                  </LuxPlusPriceGuard>
                </>}
              </PriceSummary>
              {!availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED && <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
                {intl.formatMessage({
                  id: 'uj5Cp48Lwa',
                  defaultMessage: 'Browse all hotel offers',
                  description: 'Button text to browse all hotel offers in sticky header',
                })}
              </TextButton>}
              {availableForPurchase &&
                <TextButton kind="primary" onClick={onViewOptionsClick}>
                  <CSSBreakpoint max="tablet" as="span">
                    {intl.formatMessage({
                      id: 'Sg/d7pv5pJ',
                      defaultMessage: 'View options',
                      description: 'Button text to view options on mobile',
                    })}
                  </CSSBreakpoint>
                  <CSSBreakpoint min="desktop" as="span">
                    {intl.formatMessage({
                      id: '9PMOi+uehE',
                      defaultMessage: 'View package options',
                      description: 'Button text to view package options on desktop',
                    })}
                  </CSSBreakpoint>
                </TextButton>}
            </PriceSummarySection>
          </WalledContent>
        </ContentWrapper>
      </LayoutContainer>
    </StyledNavWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  calendarsLoading: arePricesLoading(appState, state),
  durationLabel: getFromDurationLabel(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
  charterBundleRoute: state.charterBundleRoute,
  priceMode: state.priceMode,
  rooms: state.rooms,
}), FlashHotelStickyHeader, preventRenderWithOpenFilter)
